/**
 * #353 — Initial project status resolution (frontend mirror).
 *
 * Mirrors `mcp-server/src/services/projectStatusService.js` exactly, so the
 * two project-creation surfaces (the MCP agent's `create_project` tool and
 * this app's own forms — `ProjectForm.js` via `projectService.createProject`,
 * and `ProjectsUnifiedView`'s "Nuevo Proyecto con Cotización" via
 * `projectQuotationService.createProjectWithQuotation`) agree on what a
 * brand-new project is allowed to claim.
 *
 * ## Why this exists (do not hardcode `status: PROJECT_STATUS.QUOTATION_SENT` again)
 *
 * `quotation_sent` asserts commercial work that a brand-new project has not
 * had: dice que ya salió una cotización al cliente. Es lo que estaba
 * hardcodeado en las dos rutas de creación (ver issue #296 en el historial de
 * `projectService.js` / `projectQuotationService.js`) — un workaround por una
 * migración rota, no una decisión de negocio. Hoy 69 de 73 proyectos en prod
 * están en ese estado.
 *
 * `projectQuotationService.createProjectWithQuotation` made this worse: it
 * asserted the project as "quotation_sent" while creating the *quotation*
 * itself as `draft` (or `under_review`, i.e. pending internal approval) in
 * the very same call — the project claimed a quotation had gone out to the
 * client when the quotation row it just created says otherwise.
 *
 * ## Estado real en prod (#A8 — verificado por SELECT el 28-jul, NO editar
 * ## sin volver a verificar)
 *
 * El comentario que había acá decía que el CHECK vivo rechazaba `draft`. **Ya
 * no es cierto**: `projects_status_check` hoy es
 *   CHECK (status IN ('draft','quotation_sent','quotation_accepted',
 *                     'po_received','in_progress','closed'))
 * o sea que `draft` SÍ se acepta y la migración
 * `20260715200000_project_draft_initial_status.sql` está aplicada de hecho.
 *
 * **Y sin embargo `REACT_APP_PROJECT_INITIAL_STATUS=draft` TODAVÍA NO SE
 * PUEDE PONER.** Lo que lo bloquea ahora es otra cosa, y es una bomba de
 * tiempo, no una limitación: el trigger vivo `update_project_quoted_amount`
 * (BEFORE UPDATE sobre `quotations`, dispara cuando una cotización pasa a
 * `approved`) hace
 *
 *     UPDATE public.projects
 *        SET quoted_amount = NEW.total,
 *            status = CASE WHEN status = 'draft' THEN 'quoted' ELSE status END
 *
 * y **'quoted' no está en el CHECK**. Mientras el proyecto nazca en
 * `quotation_sent`, la rama `CASE` nunca se toma y nadie lo nota. En cuanto
 * los proyectos nazcan en `draft`, aprobar la PRIMERA cotización de cualquier
 * proyecto nuevo va a fallar con violación de constraint — el flujo central
 * del negocio, roto para todo proyecto creado desde ese build.
 *
 * **Orden obligatorio para cerrar esto:**
 *   1. Migración que corrija el trigger (`'quoted'` → `'quotation_sent'`, que
 *      es lo que el estado significa y sí está en el CHECK).
 *   2. Verificar por SELECT que `prosrc` ya no contiene `'quoted'`.
 *   3. Recién ahí `REACT_APP_PROJECT_INITIAL_STATUS=draft` en el build del
 *      frontend (y `PROJECT_INITIAL_STATUS=draft` para el MCP server, que
 *      tiene su propio espejo en `projectStatusService.js`).
 *   4. Backfill opcional de los proyectos que nunca tuvieron cotización
 *      enviada y hoy dicen `quotation_sent`.
 *
 * Invertir el orden cambia una etiqueta que miente por un error opaco de
 * Postgres en el momento de aprobar, que es peor.
 *
 * Hasta entonces este resolver sigue devolviendo el `quotation_sent`
 * provisional (comportamiento de hoy, cero regresión) pero lo dice
 * explícitamente vía `provisional: true` en vez de afirmarlo como un hecho.
 */

import { PROJECT_STATUS } from '../utils/constants';

/**
 * The initial state the schema was originally designed around, and the live
 * column DEFAULT. #A8: ya ESTÁ admitido por el CHECK vivo — lo que falta para
 * poder usarlo es arreglar el trigger `update_project_quoted_amount`, que
 * escribe `'quoted'` (valor que el CHECK NO admite). Ver el bloque de arriba.
 */
export const PROJECT_STATUS_DRAFT = 'draft';

/**
 * Fallback when nothing else is configured. Preserves pre-#353 behaviour
 * exactly, but is now reported as provisional instead of asserted as fact.
 */
export const PROVISIONAL_INITIAL_STATUS = PROJECT_STATUS.QUOTATION_SENT;

/** Every value the live CHECK accepts (PROJECT_STATUS no incluye 'draft'). */
export const ALLOWED_INITIAL_STATUSES = [...Object.values(PROJECT_STATUS), PROJECT_STATUS_DRAFT];

export const PROVISIONAL_STATUS_WARNING =
  `El proyecto se creó con status='${PROVISIONAL_INITIAL_STATUS}', que NO significa que se haya ` +
  'enviado una cotización: es un valor provisional porque la base de datos todavía no acepta un ' +
  "estado inicial neutro ('draft'). No lo muestres a Jorge como \"cotización enviada\" (issue #353).";

/**
 * Resolves the status a brand-new project must be inserted with.
 *
 * @param {Object} [env] - Environment source (injectable for tests). Defaults
 *   to CRA's build-time `process.env`.
 * @returns {{status: string, provisional: boolean, warning: string|null}}
 *   `provisional: true` means the status asserts commercial work that did not
 *   happen and must not be read/rendered as fact.
 * @throws {Error} If REACT_APP_PROJECT_INITIAL_STATUS is set to an unknown value.
 */
export const resolveInitialProjectStatus = (env = process.env) => {
  const configured = typeof env.REACT_APP_PROJECT_INITIAL_STATUS === 'string'
    ? env.REACT_APP_PROJECT_INITIAL_STATUS.trim()
    : '';

  const status = configured || PROVISIONAL_INITIAL_STATUS;

  if (!ALLOWED_INITIAL_STATUSES.includes(status)) {
    throw new Error(
      `Invalid REACT_APP_PROJECT_INITIAL_STATUS "${configured}". Allowed: ${ALLOWED_INITIAL_STATUSES.join(', ')}. ` +
      `Note '${PROJECT_STATUS_DRAFT}' is accepted by the CHECK but is NOT safe yet: the ` +
      `update_project_quoted_amount trigger writes status='quoted', which the CHECK rejects (#A8).`
    );
  }

  // 'draft' is the only value that asserts nothing. Every other value in the
  // lifecycle claims a commercial milestone a new project has not reached.
  const provisional = status !== PROJECT_STATUS_DRAFT;

  return {
    status,
    provisional,
    warning: provisional ? PROVISIONAL_STATUS_WARNING : null,
  };
};

const projectStatusResolver = {
  resolveInitialProjectStatus,
  PROJECT_STATUS_DRAFT,
  PROVISIONAL_INITIAL_STATUS,
};

export default projectStatusResolver;
